/**
 * Phone navigation (app-clarity concept `home` at 390): Needs you (amber count above the label) ·
 * the last project · (+) · Running (count above) · Settings. One line of 14 px labels, ≥ 44 px
 * targets, above the iOS home indicator. (+) opens the composer for the current tab's project (the
 * shell pre-fills it). Needs you and Running are Home's views, so there is still one model.
 */
import { Link, useRouterState } from '@tanstack/react-router'
import { List, Lock, Plus, Settings } from 'lucide-react'
import { homeHref, projectHref, projectOfPath } from '../../lib/ia.js'
import { defaultProject } from '../../lib/last-project.js'
import { placeOf, tabOfPath } from '../../lib/tab-memory.js'
import { useAppUi } from '../app-ui.js'
import { useLocked } from '../lock/ContentLock.js'
import { useProjectTabs } from '../tabs/useProjectTabs.js'

export function MobileBar() {
  const ui = useAppUi()
  const tabs = useProjectTabs()
  const loc = useRouterState({ select: (s) => s.location })
  const here = projectOfPath(loc.pathname)
  const v = loc.pathname.startsWith('/home') ? (loc.search as { v?: string }).v : undefined
  const ids = tabs.pinned.map((p) => p.id)
  const projectId = here ?? defaultProject(ids)
  const project = tabs.pinned.find((p) => p.id === projectId) ?? tabs.projects.data?.[0]
  // Locked: the counts are unknown here, so a lock stands in (never 0, never a dangling "—").
  const locked = useLocked()
  const needs = locked ? <Lock size={18} aria-hidden="true" /> : tabs.totals.needs
  const running = locked ? <Lock size={18} aria-hidden="true" /> : tabs.totals.running
  return (
    <nav className="tabbar vs-mbar" aria-label="Main">
      <Link
        to={homeHref({ v: 'needs' })}
        aria-current={v === 'needs' ? 'page' : undefined}
        aria-label={locked ? 'Needs you (locked)' : `Needs you, ${tabs.totals.needs}`}
      >
        <b className="vs-mbar__n is-wait" aria-hidden="true">
          {needs}
        </b>
        <span aria-hidden="true">Needs you</span>
      </Link>
      {project ? (
        <Link
          to={placeOf(project.id, projectHref(project.id), tabOfPath(loc.pathname))}
          aria-current={here ? 'page' : undefined}
          aria-label={`${project.name} tab`}
        >
          <List size={20} aria-hidden="true" />
          <span aria-hidden="true" className="vs-mbar__name">
            {project.name}
          </span>
        </Link>
      ) : (
        <Link to="/home" aria-current={loc.pathname === '/home' && !v ? 'page' : undefined}>
          <List size={20} aria-hidden="true" />
          <span aria-hidden="true">Home</span>
        </Link>
      )}
      <button
        type="button"
        className="capture-btn"
        aria-label={project && here ? `Tell Viko about ${project.name}` : 'Tell Viko anything'}
        onClick={() => ui.openCapture()}
      >
        <Plus size={22} strokeWidth={2.4} aria-hidden="true" />
      </button>
      <Link
        to={homeHref({ v: 'running' })}
        aria-current={v === 'running' ? 'page' : undefined}
        aria-label={locked ? 'Running (locked)' : `Running, ${tabs.totals.running}`}
      >
        <b className="vs-mbar__n" aria-hidden="true">
          {running}
        </b>
        <span aria-hidden="true">Running</span>
      </Link>
      <Link
        to="/settings"
        aria-current={loc.pathname.startsWith('/settings') ? 'page' : undefined}
        aria-label="Settings"
      >
        <Settings size={20} aria-hidden="true" />
        <span aria-hidden="true">Settings</span>
      </Link>
    </nav>
  )
}
